import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import s from './motion.module.css';

/** Số khối tối đa có độ trễ riêng (Mint-Motion mục 6). */
export const REVEAL_MAX = 6;

/** Độ trễ chỉ số cho khối thứ `i` (0-based): kẹp ở khối 6. */
export const revealIndex = (i: number) => Math.min(Math.max(i, 0), REVEAL_MAX - 1);

/** Khối trang hiện lần lượt (240 ms, cách 40 ms). Bọc từng khối với `index` tăng dần. */
export function Reveal({ index, children, className }: { index: number; children: ReactNode; className?: string }) {
  const style = { '--i': revealIndex(index) } as CSSProperties;
  return (
    <div className={[s.reveal, className].filter(Boolean).join(' ')} style={style}>
      {children}
    </div>
  );
}

/** Dấu đóng khi chốt việc (BL Released · Đã thu · Đã duyệt · Won). `shown=false` gỡ dấu mờ dần. */
export function Stamp({ shown, children, className }: { shown: boolean; children: ReactNode; className?: string }) {
  if (!shown) return null;
  return <span className={[s.stamp, className].filter(Boolean).join(' ')}>{children}</span>;
}

/** Ba chấm "đang gõ" (Mint-Motion mục 8). */
export function TypingDots({ label, className }: { label: string; className?: string }) {
  return (
    <span className={[s.dots, className].filter(Boolean).join(' ')} role="img" aria-label={label}>
      <i /><i /><i />
    </span>
  );
}

/** Lớp CSS cho bong bóng chat mới nảy lên (180 ms). */
export const chatBubbleClass = s.bubble;

/** Nội suy easeOutCubic từ `from` tới `to` tại tiến độ `p` ∈ [0,1]. */
export function tween(from: number, to: number, p: number) {
  const k = Math.min(Math.max(p, 0), 1);
  const e = 1 - Math.pow(1 - k, 3);
  return from + (to - from) * e;
}

/** Số chạy 400 ms tới `value` khi đổi; tôn trọng prefers-reduced-motion (đổi tức thì). */
export function useCountUp(value: number, ms = 400): number {
  const [v, setV] = useState(value);
  const cur = useRef(value);
  cur.current = v;
  useEffect(() => {
    const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || typeof requestAnimationFrame !== 'function') {
      setV(value);
      return;
    }
    const from = cur.current;
    const t0 = performance.now();
    let id = 0;
    const step = (t: number) => {
      const p = (t - t0) / ms;
      setV(p >= 1 ? value : tween(from, value, p));
      if (p < 1) id = requestAnimationFrame(step);
    };
    id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(id);
  }, [value, ms]);
  return v;
}

/** Nội suy từng phần tử mảng điểm (cùng độ dài) tại tiến độ `p`. */
export function tweenPoints(from: number[], to: number[], p: number): number[] {
  return to.map((v, i) => tween(from[i] ?? v, v, p));
}

/** Đường gấp khúc SVG "M x y L …" cho dãy giá trị, `x(i)` / `y(v)` do chỗ gọi cấp. */
export function linePath(values: number[], x: (i: number) => number, y: (v: number) => number): string {
  return values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
}

/** Biểu đồ uốn: đổi `target` thì các điểm chạy từ dạng cũ sang mới 320 ms out (tween điểm, không vẽ lại). */
export function useTweenPoints(target: number[], ms = 320): number[] {
  const [v, setV] = useState(target);
  const cur = useRef(target);
  cur.current = v;
  const key = target.join(',');
  useEffect(() => {
    const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || typeof requestAnimationFrame !== 'function') {
      setV(target);
      return;
    }
    const from = cur.current;
    const t0 = performance.now();
    let id = 0;
    const step = (t: number) => {
      const p = (t - t0) / ms;
      setV(p >= 1 ? target : tweenPoints(from, target, p));
      if (p < 1) id = requestAnimationFrame(step);
    };
    id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ms]);
  return v;
}

/** Chuyển thẻ `id` sang cột `to` (thẻ đứng cuối cột mới). Trả mảng cũ nếu thẻ không đổi cột. */
export function moveCard<T extends { id: string; col: number }>(cards: T[], id: string, to: number): T[] {
  const c = cards.find((x) => x.id === id);
  if (!c || c.col === to) return cards;
  return cards.filter((x) => x.id !== id).concat([{ ...c, col: to }]);
}

/**
 * Kéo thẻ đổi cột (Mint-Motion mục 2): chuột kéo, nút → chuyển cột kế (bàn phím / chạm).
 * Màn gắn `cardProps(id)` vào thẻ (mờ 40% + nét đứt khi `dragging === id`), `colProps(i)` vào cột
 * (sáng viền khi `over === i`); `onMove(id, to, from)` gọi khi thả — màn đẩy toast Hoàn tác.
 */
export function useCardDrag(onMove: (id: string, to: number, from: number) => void, colOf: (id: string) => number) {
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const reset = () => {
    setDragging(null);
    setOver(null);
  };
  const move = (id: string, to: number) => {
    const from = colOf(id);
    reset();
    if (from !== to) onMove(id, to, from);
  };
  return {
    dragging,
    over,
    move,
    cardProps: (id: string) => ({
      draggable: true as const,
      onDragStart: (e: React.DragEvent) => {
        try {
          e.dataTransfer.setData('text/plain', id);
          e.dataTransfer.effectAllowed = 'move';
        } catch {
          /* trình duyệt cũ */
        }
        setDragging(id);
      },
      onDragEnd: reset,
    }),
    colProps: (i: number) => ({
      onDragOver: (e: React.DragEvent) => {
        e.preventDefault();
        if (over !== i) setOver(i);
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        if (dragging) move(dragging, i);
      },
    }),
  };
}
